'use client';

/**
 * HUB D'ACCUEIL — version GOLDEN LUXE PRESTIGE :
 *  1. Hero doré — bismillah, badge, titre, particules scintillantes, double bande de stats
 *  2. 5 Royaumes du Savoir — Sciences · Miracles · Salle Royale · Espace Royal · Univers 2500
 *  3. 4 grandes cartes de niveaux — Foriji · Digdaum · Hifz · Kang (→ Académie)
 *  4. Grille d'accès rapide — toutes les vues de la plateforme
 */
import { useMemo } from 'react';
import { useApp, type Section, type AcademyLevel } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import { LANGS_COUNT } from '@/lib/quran-data';
import {
  BookOpen, Headphones, GraduationCap, PenLine, Sparkles, ScrollText, Landmark,
  Compass, CalendarDays, Video, Wrench, Images, Ear, Volume2, Brain, Crown, ChevronRight,
  Library, Atom, Radio, Users, Gem, BookMarked,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface LevelCard {
  key: AcademyLevel;
  name: string;
  arabic: string;
  badge: string;
  desc: string;
  icon: typeof Ear;
  ring: string;
  glow: string;
  chip: string;
}

interface QuickCard {
  key: Section;
  label: string;
  desc: string;
  icon: React.ReactNode;
}

export default function Hero() {
  const { uiLang, setSection, setAcademyLevel } = useApp();
  const t = DICTS[uiLang];
  const rtl = uiLang === 'ar';

  const openLevel = (lv: AcademyLevel) => {
    setAcademyLevel(lv);
    setSection('academy');
  };

  /* Particules dorées déterministes */
  const particles = useMemo(
    () => Array.from({ length: 26 }, (_, i) => {
      const s = (i * 3313) % 991;
      return {
        left: s % 100,
        top: (s * 5) % 100,
        size: 1.5 + (s % 3),
        delay: (s % 40) / 10,
      };
    }),
    []
  );

  /* ===== 5 Royaumes du Savoir ===== */
  const realms: QuickCard[] = [
    { key: 'sciences', label: t.nav.sciences, desc: t.hub.sciences_desc, icon: <Library className="h-6 w-6" /> },
    { key: 'miracles', label: t.nav.miracles, desc: t.hub.miracles_desc, icon: <Atom className="h-6 w-6" /> },
    { key: 'conference', label: t.nav.conference, desc: t.hub.conference_desc, icon: <Radio className="h-6 w-6" /> },
    { key: 'espace', label: t.nav.espace, desc: t.hub.espace_desc, icon: <Users className="h-6 w-6" /> },
    { key: 'univers', label: t.nav.univers, desc: t.hub.univers_desc, icon: <Gem className="h-6 w-6" /> },
  ];

  /* ===== 4 cartes de niveaux ===== */
  const levelCards: LevelCard[] = [
    {
      key: 'foriji', name: 'Foriji', arabic: 'الفُرْجِي',
      badge: t.hub.foriji_badge, desc: t.hub.foriji_desc, icon: Ear,
      ring: 'hover:border-[#d4af37]/70', glow: 'group-hover:shadow-[0_16px_50px_rgba(212,175,55,0.22)]',
      chip: 'border-[#d4af37]/40 bg-[rgba(212,175,55,0.12)] text-[#f0d98c]',
    },
    {
      key: 'digdaum', name: 'Digdaum', arabic: 'الدِّدْغَام',
      badge: t.hub.digdaum_badge, desc: t.hub.digdaum_desc, icon: Volume2,
      ring: 'hover:border-[#2e8b6e]/80', glow: 'group-hover:shadow-[0_16px_50px_rgba(46,139,110,0.25)]',
      chip: 'border-[#2e8b6e]/45 bg-[rgba(46,139,110,0.14)] text-[#7fd6b4]',
    },
    {
      key: 'hifz', name: 'Hifz', arabic: 'حِفْظ',
      badge: t.hub.hifz_badge, desc: t.hub.hifz_desc, icon: Brain,
      ring: 'hover:border-[#3f8fa8]/80', glow: 'group-hover:shadow-[0_16px_50px_rgba(63,143,168,0.22)]',
      chip: 'border-[#3f8fa8]/45 bg-[rgba(63,143,168,0.14)] text-[#8fd4e8]',
    },
    {
      key: 'kang', name: 'Kang', arabic: 'كَانْغ',
      badge: t.hub.kang_badge, desc: t.hub.kang_desc, icon: Crown,
      ring: 'hover:border-[#8b6fc9]/80', glow: 'group-hover:shadow-[0_16px_50px_rgba(139,111,201,0.22)]',
      chip: 'border-[#8b6fc9]/45 bg-[rgba(139,111,201,0.14)] text-[#c4b0ec]',
    },
  ];

  /* ===== Grille d'accès rapide ===== */
  const quickCards: QuickCard[] = [
    { key: 'livre', label: t.nav.livre, desc: t.hub.livre_desc, icon: <BookMarked className="h-5 w-5" /> },
    { key: 'sourates', label: t.nav.sourates, desc: t.hub.sourates_desc, icon: <ScrollText className="h-5 w-5" /> },
    { key: 'read', label: t.nav.read, desc: t.hub.read_desc, icon: <BookOpen className="h-5 w-5" /> },
    { key: 'listen', label: t.nav.listen, desc: t.hub.listen_desc, icon: <Headphones className="h-5 w-5" /> },
    { key: 'learn', label: t.nav.learn, desc: t.hub.learn_desc, icon: <GraduationCap className="h-5 w-5" /> },
    { key: 'write', label: t.nav.write, desc: t.hub.write_desc, icon: <PenLine className="h-5 w-5" /> },
    { key: 'names', label: t.nav.names, desc: t.hub.names_desc, icon: <Sparkles className="h-5 w-5" /> },
    { key: 'videos', label: t.nav.videos, desc: t.hub.videos_desc, icon: <Video className="h-5 w-5" /> },
    { key: 'qibla', label: t.nav.qibla, desc: t.hub.qibla_desc, icon: <Compass className="h-5 w-5" /> },
    { key: 'hijri', label: t.nav.hijri, desc: t.hub.hijri_desc, icon: <CalendarDays className="h-5 w-5" /> },
    { key: 'tools', label: t.nav.tools, desc: t.hub.tools_desc, icon: <Wrench className="h-5 w-5" /> },
    { key: 'gallery', label: t.nav.univers, desc: t.hub.univers_desc, icon: <Images className="h-5 w-5" /> },
  ];

  return (
    <>
      {/* ================= HERO ================= */}
      <section className="pattern-islamic relative overflow-hidden border-b border-[rgba(212,175,55,0.25)]">
        {/* Ornamental rays */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 55% 45% at 50% 0%, rgba(212,175,55,0.14), transparent 65%), radial-gradient(ellipse 40% 30% at 15% 85%, rgba(46,139,110,0.10), transparent 70%), radial-gradient(ellipse 40% 30% at 85% 85%, rgba(212,175,55,0.07), transparent 70%)',
          }}
        />

        {/* Particules dorées */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          {particles.map((p, i) => (
            <span key={i} className="twinkle absolute rounded-full bg-[#f7e7b0]"
              style={{ left: `${p.left}%`, top: `${p.top}%`, width: p.size, height: p.size, animationDelay: `${p.delay}s` }} />
          ))}
          {/* Grande étoile à 8 branches en filigrane */}
          <span className="star-ornament float-soft-slow absolute -right-14 top-14 h-56 w-56 bg-gradient-to-br from-[rgba(212,175,55,0.14)] to-transparent" />
          <span className="star-ornament float-soft absolute -left-20 bottom-6 h-44 w-44 bg-gradient-to-tl from-[rgba(212,175,55,0.10)] to-transparent" />
        </div>

        <div className="relative mx-auto max-w-5xl px-4 py-14 text-center sm:py-20">
          {/* Bismillah */}
          <p className="font-arabic mb-6 text-xl text-[#f0d98c]/85 sm:text-2xl" dir="rtl" lang="ar">
            بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
          </p>

          {/* Badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)] px-4 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[#d4af37]" aria-hidden />
            <span className="text-xs font-semibold tracking-[0.22em] text-[#f0d98c] uppercase">
              {t.hero.badge}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-serif-royal mb-4 text-5xl font-bold leading-[1.05] sm:text-6xl md:text-7xl">
            <span className="text-gold-gradient">THE QURAN</span>
            <br />
            <span className="text-gold-gradient">IN 1000 LANGUAGES</span>
          </h1>

          {rtl && (
            <p className="font-arabic mb-3 text-2xl text-[#f0d98c]" lang="ar" dir="rtl">
              القرآن الكريم بألف لغة
            </p>
          )}

          <p className="mx-auto mb-10 max-w-2xl text-base leading-relaxed text-[#cfe0cf]/90 sm:text-lg">
            {t.hero.subtitle}
          </p>

          {/* Stats — Coran */}
          <div className="mx-auto grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            {[
              { v: '1000', l: t.hero.stat_lang_goal },
              { v: String(LANGS_COUNT), l: t.hero.stat_lang_now },
              { v: '114', l: t.hero.stat_surahs },
              { v: '6236', l: t.hero.stat_ayahs },
            ].map((s) => (
              <div key={s.l} className="gold-ornate-card rounded-xl px-3 py-5">
                <div className="font-serif-royal text-3xl font-bold text-[#f0d98c] sm:text-4xl">{s.v}</div>
                <div className="mt-1 text-[11px] font-medium tracking-wide text-[#cfe0cf]/70 uppercase">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Stats — Royaumes */}
          <div className="mx-auto mt-3 grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            {[
              { v: '29', l: t.hero.stat_editions },
              { v: '4', l: t.hero.stat_levels },
              { v: '10+', l: t.hero.stat_sciences },
              { v: '2500', l: t.hero.stat_products },
            ].map((s) => (
              <div key={s.l} className="glass-gold rounded-xl px-3 py-4">
                <div className="font-serif-royal text-2xl font-bold text-[#7fd6b4] sm:text-3xl">{s.v}</div>
                <div className="mt-0.5 text-[10px] font-medium tracking-wide text-[#cfe0cf]/65 uppercase">{s.l}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-[#cfe0cf]/50">{t.hero.stat_note}</p>
        </div>
      </section>

      {/* ================= 5 ROYAUMES DU SAVOIR ================= */}
      <section className="relative border-b border-[rgba(212,175,55,0.2)] bg-[rgba(2,25,19,0.45)] py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center">
            <p className="text-sm font-medium uppercase tracking-[0.3em] text-[#d4af37]/85">
              {t.hub.realms_eyebrow}
            </p>
            <h2 className="mt-3 font-serif-royal text-3xl font-bold sm:text-4xl">
              <span className="text-gold-gradient">{t.hub.realms_title}</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-[#cfe0cf]/75 sm:text-base">
              {t.hub.realms_sub}
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {realms.map((r, i) => (
              <button
                key={r.key}
                onClick={() => setSection(r.key)}
                className={cn(
                  'glass-gold glass-gold-hover group relative flex flex-col overflow-hidden rounded-2xl p-6 text-left',
                  r.key === 'univers' && 'sm:col-span-2 lg:col-span-1'
                )}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute -right-3 -top-6 font-serif-royal text-[86px] font-bold leading-none text-[rgba(212,175,55,0.06)]"
                >
                  {i + 1}
                </span>
                <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.1)] text-[#d4af37] transition-transform duration-300 group-hover:scale-110">
                  {r.icon}
                </span>
                <span className="text-lg font-bold text-[#f5f0e1] group-hover:text-[#f0d98c]">{r.label}</span>
                <span className="mt-1.5 flex-1 text-[12.5px] leading-relaxed text-[#cfe0cf]/65">{r.desc}</span>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#f0d98c] transition-transform duration-300 group-hover:translate-x-1">
                  {t.hub.realms_cta}
                  <ChevronRight className="h-4 w-4 rtl-flip" aria-hidden />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ================= 4 NIVEAUX (Académie) ================= */}
      <section className="pattern-islamic relative border-b border-[rgba(212,175,55,0.2)] py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center">
            <p className="text-sm font-medium uppercase tracking-[0.3em] text-[#d4af37]/85">NDIAMSYL PEYE Academy</p>
            <h2 className="mt-3 font-serif-royal text-3xl font-bold sm:text-4xl">
              <span className="text-gold-gradient">{t.hub.levels_title}</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-[#cfe0cf]/75 sm:text-base">
              {t.hub.levels_sub}
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {levelCards.map((lv, i) => {
              const Icon = lv.icon;
              return (
                <button
                  key={lv.key}
                  onClick={() => openLevel(lv.key)}
                  className={cn(
                    'group relative flex flex-col overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.28)] bg-gradient-to-b from-[rgba(7,53,40,0.85)] to-[rgba(3,32,25,0.95)] p-6 text-left transition-all duration-300 hover:-translate-y-1.5',
                    lv.ring,
                    lv.glow
                  )}
                  style={{ animationDelay: `${i * 90}ms` }}
                >
                  <span className="pointer-events-none absolute -right-3 -top-6 font-serif-royal text-[92px] font-bold leading-none text-[rgba(212,175,55,0.07)]" aria-hidden>
                    {i + 1}
                  </span>

                  <div className="mb-4 flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.1)] text-[#d4af37] transition-transform duration-300 group-hover:scale-110">
                      <Icon className="h-6 w-6" />
                    </span>
                    <span className="font-arabic text-2xl text-[#f0d98c]/85" dir="rtl" lang="ar">{lv.arabic}</span>
                  </div>

                  <span className={cn('mb-2 inline-flex w-fit items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider', lv.chip)}>
                    {lv.badge}
                  </span>
                  <h3 className="font-serif-royal text-2xl font-bold text-[#f5f0e1]">{lv.name}</h3>
                  <p className="mt-2 flex-1 text-[13px] leading-relaxed text-[#cfe0cf]/70">{lv.desc}</p>

                  <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#f0d98c] transition-transform duration-300 group-hover:translate-x-1">
                    {t.hub.levels_cta}
                    <ChevronRight className="h-4 w-4 rtl-flip" aria-hidden />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= ACCÈS RAPIDE ================= */}
      <section className="relative py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-8 text-center">
            <h2 className="font-serif-royal text-2xl font-bold sm:text-3xl">
              <span className="text-gold-gradient">{t.hub.quick_title}</span>
            </h2>
            <p className="mt-2 text-sm text-[#cfe0cf]/70">{t.hub.quick_sub}</p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {quickCards.map((c) => (
              <button
                key={c.key}
                onClick={() => setSection(c.key)}
                className="group flex items-center gap-4 rounded-xl border border-[rgba(212,175,55,0.22)] bg-[rgba(7,53,40,0.55)] p-4 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-[rgba(212,175,55,0.55)] hover:bg-[rgba(212,175,55,0.08)]"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[rgba(212,175,55,0.3)] bg-[rgba(212,175,55,0.08)] text-[#d4af37] transition-transform duration-300 group-hover:scale-110">
                  {c.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-bold text-[#f5f0e1] group-hover:text-[#f0d98c]">{c.label}</span>
                  <span className="mt-0.5 block truncate text-[12px] leading-snug text-[#cfe0cf]/60">{c.desc}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#d4af37]/50 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-[#d4af37] rtl-flip" aria-hidden />
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
